import React, {RefObject, useCallback, useMemo} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import {useTranslation} from 'react-i18next';
import {useVariables} from '../../../../assets/objects/Variables';
import {convertVariablesToHtml} from '../../../textEditor/Variables.js';
import {SANITIZE_CONFIG} from '../../../textEditor/TextEditorHelpers.js';
import * as css from './WhatsAppFreeMessageEditor.css';
import {useFreeMessageEditor} from '../../hooks/useFreeMessageEditor';
import {WHATSAPP_MAX_TEXT_LENGTH} from '../../../../pages/branding/whatsAppScreen/whatsAppValidation';
import WhatsAppButtonRow from '../whatsAppButtonRow/WhatsAppButtonRow';
import {ButtonTemplateItem} from '../../types/templateStructure';
import {convertWhatsAppFormatToHtml} from '../../utils/formatUtils';

interface WhatsAppFreeMessageEditorProps {
    quillRef: RefObject<ReactQuill | null>;
    defaultText: string;
    onChange: (text: string) => void;
    variablesType: string;
    direction: 'ltr' | 'rtl';
    templateButtonItems?: ButtonTemplateItem[];
}

function WhatsAppFreeMessageEditor({
    quillRef,
    defaultText,
    onChange,
    variablesType,
    direction,
    templateButtonItems = [],
}: WhatsAppFreeMessageEditorProps) {
    const {t: tVariables} = useTranslation('variables');
    const {defaultVariables, disabledVariables} = useVariables(variablesType);
    const variables = useMemo(() => ({...defaultVariables, ...disabledVariables}), [defaultVariables, disabledVariables]);

    const mapContentToEditorHtml = useCallback(
        (raw: string) => {
            if(!raw?.trim()) {
                return '';
            }
            let htmlWithValues = convertVariablesToHtml(raw, variables, tVariables);
            htmlWithValues = convertWhatsAppFormatToHtml(htmlWithValues);
            htmlWithValues = htmlWithValues.replace(/<p><\/p>/g, '<p><br></p>');
            return DOMPurify.sanitize(htmlWithValues, SANITIZE_CONFIG);
        },
        [variables, tVariables],
    );

    const modules = {
        toolbar: false,
        history: {
            delay: 500,
            maxStack: 100,
            userOnly: false,
        },
    };

    const {handleTextChange, handleBlur, handlePasteCapture} = useFreeMessageEditor({
        content: defaultText,
        quillRef,
        onChange,
        mapContentToEditorHtml,
        maxPasteChars: WHATSAPP_MAX_TEXT_LENGTH,
    });

    return (
        <>
            <css.quillWrap
                data-cy="whatsapp-free-message"
                direction={direction}
                onPasteCapture={handlePasteCapture}
            >
                <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    modules={modules}
                    onChange={handleTextChange}
                    onBlur={handleBlur}
                />
            </css.quillWrap>
            {templateButtonItems.map((item, index) => (
                <WhatsAppButtonRow
                    key={`free-template-button-${index}-${item.value}`}
                    buttons={[{value: item.value}]}
                />
            ))}
        </>
    );
}

export default WhatsAppFreeMessageEditor;
